import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Stack,
  Box,
  Text,
  Grid,
  GridItem,
  Checkbox,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import { CreatableSelect, OptionBase, GroupBase } from "chakra-react-select";
import { userService } from "../../lib/services/userService";
import { PromoResponse, UpdatePromoRequest } from "../../lib/services/promoService";

interface PatientOption extends OptionBase {
  label: string;
  value: string;
}

interface EditPromotionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (id: number, data: UpdatePromoRequest) => void;
  promotion: PromoResponse | null; 
}

interface PromotionData {
  header: string;
  body: string;
  startDate: string;
  endDate: string;
  sendToAll: boolean;
  clientList: string[];
}

export default function EditPromotionModal({
  isOpen,
  onClose,
  onUpdate,
  promotion,
}: EditPromotionModalProps) {
  const toast = useToast();

  const [formData, setFormData] = useState<PromotionData>({
    header: "",
    body: "",
    startDate: "",
    endDate: "",
    sendToAll: false,
    clientList: [],
  });

  const [patientOptions, setPatientOptions] = useState<PatientOption[]>([]);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [recipientType, setRecipientType] = useState<"specific" | "all">("specific");

  const [errors, setErrors] = useState<{
    header?: string;
    body?: string;
    startDate?: string;
    endDate?: string;
  }>({});

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Check if the current promotion is a draft
  const isDraft = promotion?.status === 0;

  // Helper to format dates correctly for inputs 
  const formatDateForInput = (dateString: string | null) => {
    if (!dateString) return "";
    return dateString.split('T')[0].split(' ')[0];
  };

  useEffect(() => {
    if (isOpen && promotion) {
      setFormData({
        header: promotion.promo_header,
        body: promotion.promo_body,
        startDate: formatDateForInput(promotion.promo_duration_start), 
        endDate: formatDateForInput(promotion.promo_duration_end),
        sendToAll: promotion.sent_type === 'all',
        clientList: promotion.client_list 
          ? promotion.client_list.split(',').map(id => id.trim()) 
          : [],
      });
      setRecipientType(promotion.sent_type);
      fetchPatients();
    }
  }, [isOpen, promotion]);

  const fetchPatients = async () => {
    setIsLoadingPatients(true);
    try {
      const patients = await userService.getActivePatients();
      const formattedOptions = patients.map((patient: any) => ({
        label: patient.user_fullname || `${patient.user_firstname} ${patient.user_lastname}`,
        value: String(patient.id),
      }));
      setPatientOptions(formattedOptions);
    } catch (error) {
      console.error("Failed to fetch patients", error);
      toast({
        title: "Error fetching patients",
        description: "Could not load the client list.",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoadingPatients(false);
    }
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};
    let isValid = true;

    if (!formData.header.trim()) {
      newErrors.header = "Promotion header is required";
      isValid = false;
    }
    if (!formData.body.trim()) {
      newErrors.body = "Promotion body is required";
      isValid = false;
    }
    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
      isValid = false;
    }
    if (!formData.endDate) {
      newErrors.endDate = "End date is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handler for "Save as Draft" button
  const handleSaveAsDraft = () => {
    setIsSubmitted(true);
    if (!validateForm()) return;
    if (!promotion) return;

    setIsSaving(true);

    const updateData: UpdatePromoRequest = {
        promo_header: formData.header,
        promo_body: formData.body,
        promo_duration_start: formData.startDate,
        promo_duration_end: formData.endDate,
        send_to_all: recipientType === 'all',
        client_list: recipientType === 'all' ? undefined : formData.clientList.join(','),
        is_draft: true 
    };

    onUpdate(promotion.id, updateData);
  };

  // Handler for Primary Button
  const handleSubmit = () => {
    setIsSubmitted(true);
    if (!validateForm()) return;
    if (!promotion) return;

    setIsSaving(true);

    const updateData: UpdatePromoRequest = {
        promo_header: formData.header,
        promo_body: formData.body,
        promo_duration_start: formData.startDate,
        promo_duration_end: formData.endDate,
        send_to_all: recipientType === 'all',
        client_list: recipientType === 'all' ? undefined : formData.clientList.join(','),
        is_draft: false 
    };

    onUpdate(promotion.id, updateData);
  };

  const handleCancel = () => {
    setErrors({});
    setIsSubmitted(false);
    setIsSaving(false);
    onClose();
  };

  const handleInputChange = (field: keyof PromotionData, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field as keyof typeof errors]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  const inputStyles = {
    bg: "white",
    borderColor: "gray.300",
    _hover: { borderColor: "gray.400" },
    _focus: { borderColor: "#DC3611", boxShadow: "0 0 0 1px #DC3611" },
    borderRadius: "md",
    fontSize: "sm",
  };

  const labelStyles = {
    fontSize: "sm",
    fontWeight: "700",
    color: "gray.800",
    mb: 2,
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", md: "3xl" }}
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="lg" mx={{ base: 0, md: 4 }} maxH="90vh">
        <ModalHeader
          borderBottom="1px"
          borderColor="gray.100"
          py={4}
          px={6}
          bg="white"
        >
          <Text fontSize="lg" fontWeight="bold" color="gray.800">
            Edit Promotion
          </Text>
        </ModalHeader>
        <ModalCloseButton size="md" top={3} right={4} />

        <ModalBody py={6} px={6} bg="white">
          <VStack spacing={6} align="stretch">
            <Box>
              <Text fontSize="md" fontWeight="800" color="#1A202C" mb={4}>
                Promotion Information
              </Text>

              <VStack spacing={4} align="stretch">
                <FormControl isRequired isInvalid={isSubmitted && !!errors.header}>
                  <FormLabel {...labelStyles}>
                    Promotion Header
                  </FormLabel>
                  <Input
                    {...inputStyles}
                    placeholder=""
                    value={formData.header}
                    onChange={(e) => handleInputChange("header", e.target.value)}
                    height="42px"
                  />
                  <FormErrorMessage fontSize="xs">{errors.header}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={isSubmitted && !!errors.body}>
                  <FormLabel {...labelStyles}>
                    Promotion Body
                  </FormLabel>
                  <Textarea
                    {...inputStyles}
                    placeholder=""
                    value={formData.body}
                    onChange={(e) => handleInputChange("body", e.target.value)}
                    rows={6}
                    resize="vertical"
                    p={3}
                  />
                  <FormErrorMessage fontSize="xs">{errors.body}</FormErrorMessage>
                </FormControl>

                <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                  <GridItem>
                    <FormControl isRequired isInvalid={isSubmitted && !!errors.startDate}>
                      <FormLabel {...labelStyles}>
                        Promotion Duration Start
                      </FormLabel>
                      <Input
                        type="date"
                        {...inputStyles}
                        value={formData.startDate}
                        onChange={(e) => handleInputChange("startDate", e.target.value)}
                        height="42px"
                        css={{
                            "&::-webkit-calendar-picker-indicator": {
                              cursor: "pointer",
                              opacity: 0.6,
                              "&:hover": { opacity: 1 }
                            }
                        }}
                      />
                      <FormErrorMessage fontSize="xs">{errors.startDate}</FormErrorMessage>
                    </FormControl>
                  </GridItem>

                  <GridItem>
                    <FormControl isRequired isInvalid={isSubmitted && !!errors.endDate}>
                      <FormLabel {...labelStyles}>
                        Promotion Duration End
                      </FormLabel>
                      <Input
                        type="date"
                        {...inputStyles}
                        value={formData.endDate}
                        onChange={(e) => handleInputChange("endDate", e.target.value)}
                        height="42px"
                        css={{
                            "&::-webkit-calendar-picker-indicator": {
                              cursor: "pointer",
                              opacity: 0.6,
                              "&:hover": { opacity: 1 }
                            }
                        }}
                      />
                      <FormErrorMessage fontSize="xs">{errors.endDate}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                </Grid>
              </VStack>
            </Box>

            <Box>
              <Text fontSize="md" fontWeight="800" color="#1A202C" mb={4}>
                Sender Details
              </Text>

              <VStack spacing={4} align="stretch">
                <Stack direction="row" spacing={6}>
                  <Checkbox
                    isChecked={recipientType === "specific"}
                    onChange={() => setRecipientType("specific")}
                  >
                    <Text fontSize="sm" color="gray.600" fontWeight="500">
                      Send to Specific Clients
                    </Text>
                  </Checkbox>

                  <Checkbox
                    isChecked={recipientType === "all"}
                    onChange={() => setRecipientType("all")}
                  >
                    <Text fontSize="sm" color="gray.600" fontWeight="500">
                      Send to All Clients
                    </Text>
                  </Checkbox>
                </Stack>

                {recipientType === "specific" && (
                  <FormControl>
                    <FormLabel {...labelStyles}>
                      Send to Client List
                    </FormLabel>
                    <CreatableSelect<PatientOption, true, GroupBase<PatientOption>>
                      isMulti
                      isClearable
                      placeholder="Add Client"
                      options={patientOptions}
                      value={patientOptions.filter((option) =>
                        formData.clientList.includes(option.value)
                      )}
                      onChange={(newValue) => {
                        const clientIds = newValue.map((item) => item.value);
                        setFormData({ ...formData, clientList: clientIds });
                      }}
                      
                      focusBorderColor="#DC3611" 
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                      }}
                      menuPosition="fixed"
                      chakraStyles={{
                        control: (provided, state) => ({
                          ...provided,
                          bg: "white",
                          borderColor: state.isFocused ? "#DC3611" : "gray.300",
                          borderRadius: "md",
                          minHeight: "42px",
                          boxShadow: state.isFocused ? "0 0 0 1px #DC3611" : "none",
                          _hover: {
                            borderColor: state.isFocused ? "#DC3611" : "gray.400",
                          },
                        }),
                        placeholder: (provided) => ({
                            ...provided,
                            color: "gray.400",
                            fontSize: "sm"
                        }),
                        multiValue: (provided) => ({
                          ...provided,
                          borderRadius: "full",
                          bg: "gray.100",
                          border: "1px solid",
                          borderColor: "gray.200",
                        }),
                        multiValueLabel: (provided) => ({
                            ...provided,
                            fontSize: "xs",
                            color: "gray.700"
                        })
                      }}
                    />
                  </FormControl>
                )}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter bg="white" borderTop="1px" borderColor="gray.100" py={4} px={6}>
          <Stack direction="row" spacing={3} justify="flex-end" w="full">
            <Button
              variant="ghost"
              onClick={handleCancel}
              size="md"
              fontSize="sm"
              fontWeight="medium"
              color="gray.600"
              borderColor="gray.300"
              _hover={{ bg: "gray.50", borderColor: "gray.400" }}
            >
              Cancel
            </Button>

            {/* Only show "Save as Draft" if the promotion is currently a draft */}
            {isDraft && (
              <Button
                variant="ghost"
                onClick={handleSaveAsDraft}
                size="md"
                fontSize="sm"
                fontWeight="medium"
                color="gray.600"
                _hover={{ bg: "gray.50", color: "#DC3611" }}
              >
                Save as Draft
              </Button>
            )}

            <Button
              bg="#DC3611"
              color="white"
              size="md"
              fontSize="sm"
              fontWeight="medium"
              px={6}
              _hover={{ bg: "#bf3c1f" }}
              _active={{ bg: "#a0331a" }}
              onClick={handleSubmit}
            >
              {isDraft ? "Publish Promotion" : "Update Promotion"}
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}